/**
 * SmartCache - Cache with subscription pattern for auto-sync to Context
 * 
 * Design Principles:
 * - Single source of truth for all data operations
 * - Notifies subscribers on change (including React Context)
 * - Manages Firebase sync in background
 * - All operations are instant from cache
 */

class SmartCache {
  constructor() {
    // In-memory cache for instant access
    this.data = new Map();
    
    // Subscribers for change notifications
    this.subscribers = new Set();
    
    // Field-level subscribers: Map<presentationId, Map<fieldName, Set<callback>>>
    this.fieldSubscribers = new Map();
    
    // Track previous data to detect field changes
    this.previousData = new Map();
    
    // Track which presentations need Firebase sync
    this.dirtyData = new Set();
    
    // Queue for Firebase sync operations
    this.syncQueue = new Map();
    
    // Track active sync operations
    this.activeSyncs = new Map();
    
    // Sync timers for debouncing
    this.syncTimers = new Map();

    // Streaming guard: prevents Firestore overwrites during active AI streaming
    this.streamingGuardEnabled = false;
    this.activeStreamingSessions = new Map();
    this.streamingMetrics = { sessionsStarted: 0, sessionsCompleted: 0, totalDuration: 0 };

    // Live message ownership: ids of messages THIS pane is currently
    // streaming (the local turn's bubble). Remote mirror merges never
    // overwrite an owned message, and the sync loop only live-rewrites a
    // streaming message it owns. Pane-local by construction.
    this.liveMessageIds = new Set();

    console.log('SmartCache initialized');
  }

  /** Claim live authorship of a message id (this pane's streaming turn). */
  claimLiveMessage(messageId) {
    if (messageId) this.liveMessageIds.add(messageId);
  }

  /** Release live authorship (turn finalized, interrupted, or errored). */
  releaseLiveMessage(messageId) {
    if (messageId) this.liveMessageIds.delete(messageId);
  }

  /** Release every live claim (stop/interrupt/error paths). */
  releaseAllLiveMessages() {
    this.liveMessageIds.clear();
  }

  /** Whether this pane is the live author of the message. */
  isLiveMessageOwned(messageId) {
    return this.liveMessageIds.has(messageId);
  }

  /** Whether this pane currently has a live (streaming) turn writing. */
  hasLiveMessages() {
    return this.liveMessageIds.size > 0;
  }

  /**
   * Subscribe to cache changes
   * @param {Function} callback - Called with (id, data) when data changes
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  /**
   * Subscribe to specific field changes
   * @param {string} presentationId - Presentation ID
   * @param {string} fieldName - Field to watch (e.g., 'slides', 'chats')
   * @param {Function} callback - Called with field value when it changes
   * @returns {Function} Unsubscribe function
   */
  subscribeToField(presentationId, fieldName, callback) {
    // Initialize maps if needed
    if (!this.fieldSubscribers.has(presentationId)) {
      this.fieldSubscribers.set(presentationId, new Map());
    }
    const presentationFields = this.fieldSubscribers.get(presentationId);
    
    if (!presentationFields.has(fieldName)) {
      presentationFields.set(fieldName, new Set());
    }
    const fieldCallbacks = presentationFields.get(fieldName);
    
    // Add the callback
    fieldCallbacks.add(callback);
    
    // IMPORTANT: If data already exists, notify immediately
    const existingData = this.data.get(presentationId);
    if (existingData && existingData[fieldName] !== undefined) {
      console.log(`📨 SmartCache: Notifying new field subscriber with existing data for '${fieldName}'`);
      try {
        callback(existingData[fieldName]);
      } catch (error) {
        console.error(`Error notifying new field subscriber for ${fieldName}:`, error);
      }
    }
    
    // Return unsubscribe function
    return () => {
      fieldCallbacks.delete(callback);
      // Clean up empty sets/maps
      if (fieldCallbacks.size === 0) {
        presentationFields.delete(fieldName);
      }
      if (presentationFields.size === 0) {
        this.fieldSubscribers.delete(presentationId);
      }
    };
  }

  /**
   * Detect which fields changed between old and new data
   * @param {Object} oldData - Previous data
   * @param {Object} newData - New data
   * @returns {Set<string>} Set of changed field names
   */
  detectChangedFields(oldData, newData) {
    const changedFields = new Set();
    
    // Helper function to safely stringify values
    const safeStringify = (value, seen = new WeakSet()) => {
      try {
        // Handle primitives
        if (value === null || value === undefined) return String(value);
        if (typeof value !== 'object') return JSON.stringify(value);
        
        // Check for circular references
        if (seen.has(value)) {
          return '[Circular Reference]';
        }
        
        // Check for DOM elements or React internal objects
        if (value instanceof Element || value instanceof Window || 
            value._reactInternalFiber || value._reactFiber || 
            value.$$typeof || value._owner) {
          return '[DOM/React Object]';
        }
        
        // For arrays and objects, use limited depth stringify
        seen.add(value);
        if (Array.isArray(value)) {
          const result = JSON.stringify(value.slice(0, 100)); // Limit array size
          seen.delete(value);
          return result;
        }
        
        // For objects, only stringify safe properties
        const safeObj = {};
        const keys = Object.keys(value).slice(0, 100); // Limit number of keys
        for (const key of keys) {
          if (key.startsWith('_react') || key.startsWith('__react') ||
              key === 'window' || key === 'document' || key === 'target' ||
              key === 'currentTarget' || key === 'nativeEvent') {
            continue;
          }
          try {
            const val = value[key];
            if (typeof val === 'object' && val !== null) {
              safeObj[key] = '[Object]'; // Don't recurse deeply
            } else {
              safeObj[key] = val;
            }
          } catch (e) {
            safeObj[key] = '[Error accessing property]';
          }
        }
        const result = JSON.stringify(safeObj);
        seen.delete(value);
        return result;
      } catch (error) {
        // If JSON.stringify fails (circular references, etc.), treat as changed
        console.warn('SmartCache: Unable to stringify value for comparison', error.message);
        return null;
      }
    };
    
    // Check all fields in new data
    for (const field in newData) {
      if (field === 'lastModified' || field === 'updatedAt') continue; // Skip metadata

      // Special handling for critical arrays - compare by IDs, length, and content
      // This ensures instant UI updates for chats
      if (field === 'chats' && Array.isArray(newData[field])) {
        const oldArrayOrRef = oldData?.[field];
        const newArray = newData[field];

        // oldArrayOrRef might be an optimized {length, _ref} object from previous notification
        const oldArray = oldArrayOrRef?._ref || oldArrayOrRef;

        // Always mark as changed if:
        // 1. No old data
        // 2. Length changed
        // 3. Item IDs changed
        if (!oldArray ||
            !Array.isArray(oldArray) ||
            oldArray.length !== newArray.length ||
            oldArray.map(item => item?.id).join(',') !== newArray.map(item => item?.id).join(',')) {
          changedFields.add(field);
          continue;
        }
      }

      // Field added or changed
      if (!oldData || oldData[field] !== newData[field]) {
        // For arrays and objects, do a deeper comparison
        const oldStr = safeStringify(oldData?.[field]);
        const newStr = safeStringify(newData[field]);

        // If either stringify failed or values are different, mark as changed
        if (oldStr === null || newStr === null || oldStr !== newStr) {
          changedFields.add(field);
        }
      }
    }
    
    // Check for deleted fields
    if (oldData) {
      for (const field in oldData) {
        if (!(field in newData)) {
          changedFields.add(field);
        }
      }
    }
    
    return changedFields;
  }

  /**
   * Notify all subscribers of a change
   */
  notifySubscribers(id, data) {
    const startTime = performance.now();

    // Get previous data to detect changes
    const oldData = this.previousData.get(id);
    const changedFields = this.detectChangedFields(oldData, data);

    // Store current data for next comparison - use shallow clone with metadata for large fields
    try {
      const clonedData = {
        ...data,
        // For chats, store a shallow reference for change detection
        chats: data.chats ? { length: data.chats.length, _ref: data.chats } : data.chats
      };
      this.previousData.set(id, clonedData);
    } catch (error) {
      console.warn('SmartCache: Failed to clone data for comparison cache', error.message);
      // Store a shallow copy as fallback
      this.previousData.set(id, { ...data });
    }

    const cacheTime = performance.now() - startTime;
    
    // Notify general subscribers (always)
    this.subscribers.forEach(callback => {
      try {
        callback(id, data);
      } catch (error) {
        console.error('Subscriber notification error:', error);
      }
    });
    
    // Notify field-specific subscribers
    if (this.fieldSubscribers.has(id)) {
      const presentationFields = this.fieldSubscribers.get(id);

      changedFields.forEach(fieldName => {
        if (presentationFields.has(fieldName)) {
          const callbacks = presentationFields.get(fieldName);
          callbacks.forEach(callback => {
            try {
              callback(data[fieldName]);
            } catch (error) {
              console.error(`Field subscriber notification error for ${fieldName}:`, error);
            }
          });
        }
      });
    }

    const totalTime = performance.now() - startTime;
    if (totalTime > 100) {
      console.log(`SmartCache.notifySubscribers took ${totalTime.toFixed(2)}ms (cache: ${cacheTime.toFixed(2)}ms, changed: ${Array.from(changedFields).join(', ')})`);
    }
  }

  /**
   * Normalize message content into a plain string
   * @param {*} content
   * @returns {string}
   */
  normalizeMessageContent(content) {
    if (!content) {
      return '';
    }

    if (typeof content === 'string') {
      return content;
    }

    if (Array.isArray(content)) {
      return content
        .map(part => this.normalizeMessageContent(part))
        .filter(text => typeof text === 'string' && text.trim().length > 0)
        .join('\n\n');
    }

    if (typeof content === 'object') {
      if (typeof content.text === 'string') {
        return content.text;
      }

      if (typeof content.content === 'string') {
        return content.content;
      }

      if (Array.isArray(content.parts)) {
        const partText = content.parts
          .map(part => this.normalizeMessageContent(part))
          .filter(text => typeof text === 'string' && text.trim().length > 0);
        if (partText.length > 0) {
          return partText.join('\n\n');
        }
      }

      try {
        const serialized = JSON.stringify(content);
        return (serialized === '{}' || serialized === '[]') ? '' : serialized;
      } catch (error) {
        return String(content);
      }
    }

    return String(content);
  }

  /**
   * Build basic text components for a message
   * @param {Object} message
   * @returns {Array}
   */
  buildTextComponents(message) {
    const textContent = this.normalizeMessageContent(message?.content);
    if (!textContent || !textContent.trim()) {
      return [];
    }

    return [{
      type: 'text',
      content: textContent,
      timestamp: message?.timestamp || new Date().toISOString()
    }];
  }

  /**
   * Rebuild assistant components from persisted metadata
   * @param {Object} message
   * @returns {Array}
   */
  rebuildAssistantComponents(message) {
    const components = [];
    const metadata = message.metadata || {};
    const status = message.status || metadata.status;
    const timestamp = message.timestamp || new Date().toISOString();

    if (status && status !== 'complete') {
      components.push({
        type: 'thinking',
        title: 'Thinking',
        content: metadata.thinking || '',
        isLoading: true,
        isComplete: false,
        timestamp
      });
      return components;
    }

    if (metadata.thinking && typeof metadata.thinking === 'string' && metadata.thinking.trim()) {
      components.push({
        type: 'thinking',
        title: 'Thinking',
        content: metadata.thinking,
        isLoading: false,
        isComplete: true,
        timestamp
      });
    }

    const action = metadata.action || message.action || 'chat';

    if (action === 'research') {
      const citations = Array.isArray(metadata.citations)
        ? metadata.citations
        : (metadata.citations?.sources || metadata.citations || []);

      // CRITICAL FIX: Include NEW research fields (keyFindings, fullReport, sourceSummaries)
      // These fields were missing, causing research data to be lost after page reload
      const keyFindings = Array.isArray(metadata.keyFindings)
        ? metadata.keyFindings
        : (Array.isArray(metadata.researchKeyFindings) ? metadata.researchKeyFindings : []);

      const fullReport = metadata.fullReport
        || metadata.researchFullReport
        || metadata.researchReport
        || null;

      const sourceSummaries = Array.isArray(metadata.sourceSummaries)
        ? metadata.sourceSummaries
        : (Array.isArray(metadata.researchSourceSummaries) ? metadata.researchSourceSummaries : []);

      const searchQueries = Array.isArray(metadata.searchQueries)
        ? metadata.searchQueries
        : [];

      components.push({
        type: 'research',
        data: {
          query: metadata.researchQuery || 'Research',
          keyFindings,           // NEW: Structured key findings array
          report: fullReport,    // Backwards compat: point to fullReport
          fullReport,            // NEW: Full report field
          citations,
          citationsDuration: metadata.citationsDuration || null,
          reportDuration: metadata.reportDuration || null,
          sourceSummaries,       // NEW: Source summaries array
          searchQueries,         // NEW: WebSearch queries for transparency
          isLoading: false,
          status: 'complete'
        },
        timestamp
      });
    }

    if (action === 'create_slides') {
      const createdSlides = Array.isArray(metadata.createdSlides) ? metadata.createdSlides : [];
      const slideIds = Array.isArray(metadata.createdSlideIds)
        ? metadata.createdSlideIds
        : createdSlides.map(slide => slide.id).filter(Boolean);
      const slideIndices = Array.isArray(metadata.createdSlideIndices)
        ? metadata.createdSlideIndices
        : createdSlides.map(slide => slide.index).filter(index => typeof index === 'number');
      const count = typeof metadata.slideCount === 'number'
        ? metadata.slideCount
        : (createdSlides.length || slideIds.length || null);
      const completedCount = count || slideIds.length || 0;
      const progressSlides = completedCount
        ? Array(completedCount).fill(null).map(() => ({ completed: true, inProgress: false }))
        : [];

      components.push({
        type: 'createSlides',
        data: {
          status: count ? `✅ Created ${count} slide${count === 1 ? '' : 's'}` : '✅ Created slides',
          count,
          isLoading: false,
          duration: metadata.slideDuration || null,
          slideIds,
          slideIndices,
          completedCount,
          progressSlides
        },
        timestamp
      });
    }

    if (action === 'edit_slide') {
      const editedSlides = Array.isArray(metadata.editedSlides) ? metadata.editedSlides : [];
      const editedCount = editedSlides.length || (Array.isArray(metadata.editedSlideIds) ? metadata.editedSlideIds.length : 0);
      const normalizedEdits = editedSlides
        .map(slide => ({
          slideNumber: typeof slide.slideNumber === 'number'
            ? slide.slideNumber
            : (typeof slide.index === 'number' ? slide.index + 1 : null),
          slideId: slide.id || slide.slideId || null,
          slideTitle: slide.title || slide.slideTitle || null
        }))
        .filter(entry => entry.slideNumber || entry.slideId || entry.slideTitle);
      const slideNumbers = normalizedEdits
        .map(entry => entry.slideNumber)
        .filter(num => typeof num === 'number');

      components.push({
        type: 'editSlides',
        data: {
          status: editedCount ? `✅ Updated ${editedCount} slide${editedCount === 1 ? '' : 's'}` : '✅ Slide updates applied',
          slideNumbers: slideNumbers.length > 0 ? slideNumbers : null,
          totalSlides: editedCount || null,
          completedSlides: editedCount || null,
          isLoading: false,
          editedSlides: normalizedEdits
        },
        timestamp
      });
    }

    const textComponents = this.buildTextComponents(message);
    components.push(...textComponents);

    if (components.length === 0) {
      return this.buildTextComponents(message);
    }

    return components;
  }

  /**
   * Prepare chats for cache storage by merging with existing data and normalizing message shape
   * @param {Array} chats - Chats retrieved from backend/cache update
   * @param {Array} existingChats - Chats currently stored in cache
   * @returns {Array}
   */
  normalizeChatsForCache(chats = [], existingChats = []) {
    const existingChatMap = new Map((existingChats || []).map(chat => [chat.id, chat]));

    // DEBUG: Track research components through normalization
    const incomingResearch = chats.flatMap(c => c.messages || [])
      .flatMap(m => m.components || [])
      .filter(comp => comp.type === 'research');

    if (incomingResearch.length > 0) {
      console.log('🔍 normalizeChatsForCache: Incoming research components:', {
        count: incomingResearch.length,
        components: incomingResearch.map(c => ({
          hasData: !!c.data,
          hasReport: !!(c.data?.report || c.data?.fullReport),
          hasKeyFindings: Array.isArray(c.data?.keyFindings) && c.data.keyFindings.length > 0,
          reportLength: (c.data?.report || c.data?.fullReport || '').length,
          dataKeys: Object.keys(c.data || {})
        }))
      });
    }

    const normalizedChats = (chats || []).map(chat => {
      const previousChat = existingChatMap.get(chat.id);
      const previousMessages = previousChat?.messages || [];
      const previousMessageMap = new Map(previousMessages.map(msg => [msg.id, msg]));

      const normalizedMessages = (chat.messages || []).map(message => {
        if (!message) return null;
        const previousMessage = previousMessageMap.get(message.id);

        // DEBUG: Log if this message has research components
        const hasIncomingResearch = message.components?.some(c => c.type === 'research');
        const hasPreviousResearch = previousMessage?.components?.some(c => c.type === 'research');

        // Merge with previous message to retain streaming components when possible
        const merged = {
          ...(previousMessage || {}),
          ...message
        };

        // Normalize sender role
        if (!merged.sender) {
          if (merged.role === 'assistant') {
            merged.sender = 'ai';
          } else if (merged.role === 'user') {
            merged.sender = 'user';
          }
        }

        if (!merged.id) {
          merged.id = previousMessage?.id || `msg-${Date.now()}`;
        }

        // Preserve components if we already rendered them; otherwise create from basic content
        if (!Array.isArray(merged.components) || merged.components.length === 0) {
          if (previousMessage?.components?.length) {
            if (hasPreviousResearch) {
              console.log('🔍 normalizeChatsForCache: Preserving research from previousMessage');
            }
            merged.components = previousMessage.components;
          } else {
            if (merged.sender === 'ai' || merged.role === 'assistant') {
              const rebuilt = this.rebuildAssistantComponents(merged) || [];
              merged.components = Array.isArray(rebuilt) ? rebuilt : [];
            } else {
              merged.components = this.buildTextComponents(merged);
            }
          }
        } else if (hasIncomingResearch) {
          console.log('🔍 normalizeChatsForCache: Message has incoming research components:', {
            messageId: merged.id,
            componentCount: merged.components.length,
            researchComponent: merged.components.find(c => c.type === 'research')
          });
        }

        // Ensure metadata exists so the stream can add status info later
        if (!merged.metadata) {
          merged.metadata = previousMessage?.metadata || null;
        }

        return merged;
      }).filter(Boolean);

      return {
        ...(previousChat || {}),
        ...chat,
        messages: normalizedMessages
      };
    });

    // DEBUG: Verify research components after normalization
    const outputResearch = normalizedChats.flatMap(c => c.messages || [])
      .flatMap(m => m.components || [])
      .filter(comp => comp.type === 'research');

    if (incomingResearch.length > 0 || outputResearch.length > 0) {
      console.log('🔍 normalizeChatsForCache: Output research components:', {
        inputCount: incomingResearch.length,
        outputCount: outputResearch.length,
        preserved: outputResearch.length === incomingResearch.length,
        components: outputResearch.map(c => ({
          hasData: !!c.data,
          hasReport: !!(c.data?.report || c.data?.fullReport),
          hasKeyFindings: Array.isArray(c.data?.keyFindings) && c.data.keyFindings.length > 0,
          reportLength: (c.data?.report || c.data?.fullReport || '').length,
          hasSearchQueries: Array.isArray(c.data?.searchQueries),
          searchQueriesCount: c.data?.searchQueries?.length || 0,
          searchQueriesPreview: c.data?.searchQueries?.slice(0, 2)
        }))
      });
    }

    return normalizedChats;
  }

  /**
   * Prepare data object before storing in cache (used by set/update)
   * Slides live in PowerPoint — only normalize chats here.
   * @param {string} id
   * @param {Object} data
   * @returns {Object}
   */
  prepareDataForCache(id, data) {
    if (!data) return data;
    const existing = this.data.get(id) || {};
    const result = { ...data };
    if (result.chats) {
      result.chats = this.normalizeChatsForCache(result.chats, existing.chats || []);
    }
    return result;
  }

  /**
   * Get presentation from cache
   * @param {string} id - Presentation ID
   * @returns {Object|null} Presentation data or null
   */
  get(id) {
    return this.data.get(id) || null;
  }

  /**
   * Set presentation in cache (used after loading from Firebase)
   * @param {string} id - Presentation ID
   * @param {Object} data - Presentation data
   */
  set(id, data) {
    // Sanitize data before storing to prevent DOM contamination
    const sanitizedData = this.sanitizeData(data);
    const preparedData = this.prepareDataForCache(id, sanitizedData);

    const isFirstLoad = !this.data.has(id);
    this.data.set(id, preparedData);
    
    // For first load, we need to notify field subscribers immediately
    // because detectChangedFields won't find changes (no previous data)
    if (isFirstLoad) {
      console.log('🔄 SmartCache: First load of presentation', id);
      
      // Initialize previousData as empty for first load
      this.previousData.set(id, {});
      
      // Notify field subscribers directly for all fields
      if (this.fieldSubscribers.has(id)) {
        console.log('📢 SmartCache: Notifying field subscribers on first load');
        const presentationFields = this.fieldSubscribers.get(id);
        presentationFields.forEach((callbacks, fieldName) => {
          if (preparedData[fieldName] !== undefined) {
            console.log(`📤 SmartCache: Notifying ${callbacks.size} subscribers for field '${fieldName}'`);
            callbacks.forEach(callback => {
              try {
                callback(preparedData[fieldName]);
              } catch (error) {
                console.error(`Initial field notification error for ${fieldName}:`, error);
              }
            });
          }
        });
      }
    } else if (!this.previousData.has(id)) {
      // Safety: Initialize previousData if somehow missing
      this.previousData.set(id, {});
    }
    
    // Always notify general subscribers
    this.notifySubscribers(id, preparedData);
  }

  /**
   * Update presentation - instant cache update with background sync
   * @param {string} id - Presentation ID
   * @param {Object} updates - Partial updates to apply
   */
  update(id, updates, options = {}) {
    const current = this.data.get(id) || {};

    // Sanitize updates to prevent DOM contamination
    const sanitizedUpdates = this.sanitizeData(updates);

    const now = new Date().toISOString();
    const { skipDirtyFlag = false, preserveTimestamp = false } = options;
    const currentLastModified = typeof current.lastModified === 'string' ? current.lastModified : null;
    const nextLastModified = preserveTimestamp && currentLastModified ? currentLastModified : now;
    const updated = {
      ...current,
      ...sanitizedUpdates,
      lastModified: nextLastModified  // This is the ONLY timestamp we care about
    };

    const preparedUpdated = this.prepareDataForCache(id, updated);

    // 1. Update cache immediately
    this.data.set(id, preparedUpdated);
    if (skipDirtyFlag) {
      this.dirtyData.delete(id);
    } else {
      this.dirtyData.add(id);
    }

    // 2. Notify all subscribers (including Context)
    this.notifySubscribers(id, preparedUpdated);

    // 3. Schedule Firebase sync
    if (skipDirtyFlag) {
      if (this.syncTimers.has(id)) {
        clearTimeout(this.syncTimers.get(id));
        this.syncTimers.delete(id);
      }
    } else {
      this.scheduleSync(id);
    }

    return preparedUpdated;
  }

  /**
   * Chat Operations
   */

  /**
   * Create a new chat
   * @param {string} presentationId - Presentation ID
   * @param {Object} chatData - Chat data (name, etc)
   */
  createChat(presentationId, chatData = {}) {
    const presentation = this.get(presentationId);
    if (!presentation) return null;

    const now = new Date().toISOString();
    const defaults = {
      id: `chat-${Date.now()}`,
      name: 'New chat',
      messages: [],
      createdAt: now,
      updatedAt: now
    };

    const newChat = {
      ...defaults,
      ...chatData,
      messages: Array.isArray(chatData.messages) ? chatData.messages : defaults.messages,
      createdAt: chatData.createdAt || defaults.createdAt,
      updatedAt: chatData.updatedAt || defaults.updatedAt
    };

    const updatedChats = [...(presentation.chats || []), newChat];
    return this.update(presentationId, { 
      chats: updatedChats
    });
  }

  /**
   * Add message to chat
   * @param {string} presentationId - Presentation ID
   * @param {string} chatId - Chat ID
   * @param {Object} message - Message data
   */
  addMessage(presentationId, chatId, message) {
    const presentation = this.get(presentationId);
    if (!presentation || !presentation.chats) return null;

    const updatedChats = presentation.chats.map(chat => {
      if (chat.id !== chatId) {
        return chat;
      }

      const messageId = message.id || `msg-${Date.now()}`;
      const existingMessages = (chat.messages || []).map((msg, index) => (
        typeof msg?.sequence === 'number'
          ? msg
          : { ...msg, sequence: index }
      ));

      const highestSequence = existingMessages.reduce((max, msg) => (
        typeof msg?.sequence === 'number' && msg.sequence > max ? msg.sequence : max
      ), -1);

      const existingMessageIndex = existingMessages.findIndex(msg => msg.id === messageId);

      let updatedMessages;
      if (existingMessageIndex >= 0) {
        const preserved = existingMessages[existingMessageIndex];
        updatedMessages = existingMessages.map((msg, index) => {
          if (index !== existingMessageIndex) {
            return msg;
          }

          return {
            ...preserved,
            ...message,
            id: messageId,
            sequence: typeof preserved.sequence === 'number' ? preserved.sequence : index,
            // Timestamp is IDENTITY, not freshness: keep the creation time so
            // every pane sorts the shared transcript identically. Streaming
            // rewrites carry a fresh timestamp that must NOT advance it.
            timestamp: preserved.timestamp || message.timestamp || new Date().toISOString(),
            // Freshness lives in rev: bumped on every local write so merges
            // can prefer the newer copy of a message across panes.
            rev: Date.now()
          };
        });
      } else {
        const nextSequence = (typeof message.sequence === 'number' && message.sequence > highestSequence)
          ? message.sequence
          : highestSequence + 1;

        updatedMessages = [...existingMessages, {
          ...message,
          id: messageId,
          sequence: nextSequence,
          timestamp: message.timestamp || new Date().toISOString(),
          rev: Date.now()
        }];
      }

      const orderedMessages = [...updatedMessages].sort((a, b) => {
        const seqA = typeof a?.sequence === 'number' ? a.sequence : 0;
        const seqB = typeof b?.sequence === 'number' ? b.sequence : 0;
        if (seqA !== seqB) {
          return seqA - seqB;
        }
        const timeA = new Date(a?.timestamp || 0).getTime();
        const timeB = new Date(b?.timestamp || 0).getTime();
        return timeA - timeB;
      });

      return {
        ...chat,
        messages: orderedMessages,
        updatedAt: new Date().toISOString()
      };
    });

    return this.update(presentationId, { chats: updatedChats });
  }

  /**
   * Update chat metadata (e.g., interaction mode)
   * @param {string} presentationId - Presentation ID
   * @param {string} chatId - Chat ID
   * @param {Object} updates - Partial chat updates
   */
  updateChat(presentationId, chatId, updates = {}) {
    const presentation = this.get(presentationId);
    if (!presentation || !presentation.chats) return null;

    const hasUpdates = updates && Object.keys(updates).length > 0;
    const updatedChats = presentation.chats.map(chat => {
      if (chat.id !== chatId) {
        return chat;
      }

      const timestamp = Object.prototype.hasOwnProperty.call(updates, 'updatedAt')
        ? updates.updatedAt
        : (hasUpdates ? new Date().toISOString() : chat.updatedAt);

      const merged = {
        ...chat,
        ...updates,
        messages: Object.prototype.hasOwnProperty.call(updates, 'messages')
          ? updates.messages
          : chat.messages,
        updatedAt: timestamp || chat.updatedAt
      };

      return merged;
    });

    return this.update(presentationId, { chats: updatedChats });
  }

  /**
   * Switch to a different chat - DEPRECATED
   * UI state is now managed in UIContext, not in data layer
   * @deprecated Use UIContext.switchToChat instead
   */
  switchToChat(presentationId, chatId) {
    console.warn('switchToChat is deprecated. Chat navigation is now handled by UIContext');
    // No-op - UI state is managed separately
    return this.get(presentationId);
  }

  /**
   * Clear messages in a chat
   * @param {string} presentationId - Presentation ID
   * @param {string} chatId - Chat ID
   */
  clearChatMessages(presentationId, chatId) {
    const presentation = this.get(presentationId);
    if (!presentation || !presentation.chats) return null;

    const updatedChats = presentation.chats.map(chat => {
      if (chat.id === chatId) {
        return {
          ...chat,
          messages: []
        };
      }
      return chat;
    });

    return this.update(presentationId, { chats: updatedChats });
  }

  /**
   * Rename a chat
   * @param {string} presentationId - Presentation ID
   * @param {string} chatId - Chat ID
   * @param {string} newName - New name for the chat
   */
  renameChat(presentationId, chatId, newName) {
    const presentation = this.get(presentationId);
    if (!presentation || !presentation.chats) return null;

    const updatedChats = presentation.chats.map(chat => {
      if (chat.id === chatId) {
        return {
          ...chat,
          name: newName
        };
      }
      return chat;
    });

    return this.update(presentationId, { chats: updatedChats });
  }

  /**
   * Delete a chat
   * @param {string} presentationId - Presentation ID
   * @param {string} chatId - Chat ID to delete
   */
  deleteChat(presentationId, chatId) {
    const presentation = this.get(presentationId);
    if (!presentation || !presentation.chats) return null;

    const updatedChats = presentation.chats.filter(chat => chat.id !== chatId);
    
    // Note: currentChatId is now managed by UIContext, not data layer
    // The UI layer is responsible for switching to another chat if the current one is deleted

    return this.update(presentationId, { 
      chats: updatedChats
    });
  }

  /**
   * Update presentation title
   * @param {string} presentationId - Presentation ID
   * @param {string} title - New title
   */
  updateTitle(presentationId, title) {
    return this.update(presentationId, { title });
  }

  /**
   * Schedule Firebase sync with debouncing
   * @param {string} id - Presentation ID
   */
  scheduleSync(id) {
    // Clear existing timer
    if (this.syncTimers.has(id)) {
      clearTimeout(this.syncTimers.get(id));
    }

    // Schedule new sync after 3 seconds of inactivity
    const timer = setTimeout(() => {
      this.syncTimers.delete(id);
      // Sync will be handled by external sync service
      console.log(`Sync scheduled for presentation: ${id}`);
    }, 3000);

    this.syncTimers.set(id, timer);
  }

  /**
   * Check if presentation needs sync
   */
  isDirty(id) {
    return this.dirtyData.has(id);
  }

  /**
   * Mark presentation as synced
   */
  markSynced(id) {
    this.dirtyData.delete(id);
  }

  /**
   * Get all dirty presentations
   */
  getDirtyPresentations() {
    return Array.from(this.dirtyData);
  }

  /**
   * Clear cache for a presentation
   */
  clear(id) {
    this.data.delete(id);
    this.dirtyData.delete(id);
    if (this.syncTimers.has(id)) {
      clearTimeout(this.syncTimers.get(id));
      this.syncTimers.delete(id);
    }
    this.activeSyncs.delete(id);
  }

  /**
   * Remove a presentation from cache
   * @param {string} id - Presentation ID
   */
  remove(id) {
    this.data.delete(id);
    this.previousData.delete(id);
    this.fieldSubscribers.delete(id);
    this.dirtyData.delete(id);
    this.syncQueue.delete(id);
    this.activeSyncs.delete(id);
    if (this.syncTimers.has(id)) {
      clearTimeout(this.syncTimers.get(id));
      this.syncTimers.delete(id);
    }
  }

  /**
   * Clear all caches
   */
  clearAll() {
    this.data.clear();
    this.previousData.clear();
    this.fieldSubscribers.clear();
    this.dirtyData.clear();
    this.syncQueue.clear();
    this.activeSyncs.clear();
    this.syncTimers.forEach(timer => clearTimeout(timer));
    this.syncTimers.clear();
  }

  /**
   * Sanitize data to remove DOM elements, React internals, and circular references
   *
   * Identity preserving: when a value and every one of its descendants needs no
   * sanitization, the ORIGINAL reference is returned instead of a copy. Only the
   * path that actually changed is reallocated (structural sharing). React memo
   * comparators downstream compare by identity, so rewriting untouched objects
   * made every message re-render on every streaming delta.
   *
   * @param {*} data - Data to sanitize
   * @param {WeakSet} seen - Track visited objects
   * @returns {*} Sanitized data
   */
  sanitizeData(data, seen = new WeakSet()) {
    // Handle primitives
    if (data === null || data === undefined) return data;
    if (typeof data !== 'object') return data;
    if (data instanceof Date) return data.toISOString();
    if (data instanceof RegExp) return data.toString();

    // Check for circular references
    if (seen.has(data)) {
      console.warn('SmartCache: Circular reference detected during sanitization');
      return null;
    }
    
    // Skip DOM elements and React internals entirely
    if (data instanceof Element || data instanceof Window || 
        data instanceof Event || data instanceof Node ||
        data._reactInternalFiber || data._reactFiber || 
        data.$$typeof || data._owner || data.nativeEvent ||
        data.currentTarget || data.target || data.baseURI ||
        data.localName || data.namespaceURI || data.nodeType ||
        data.ownerDocument || data.parentNode) {
      console.warn('SmartCache: DOM/React object detected during sanitization, skipping');
      return null;
    }
    
    seen.add(data);
    
    try {
      // Handle arrays
      if (Array.isArray(data)) {
        const result = [];
        // A non plain array (subclass) is still rewritten to a plain array, as before
        let unchanged = Object.getPrototypeOf(data) === Array.prototype;
        for (let i = 0; i < data.length; i++) {
          const sanitized = this.sanitizeData(data[i], seen);
          if (sanitized !== null && sanitized !== undefined) {
            if (sanitized !== data[i]) unchanged = false;
            result.push(sanitized);
          } else {
            // Element dropped, so the array really did change
            unchanged = false;
          }
        }
        seen.delete(data);
        return unchanged ? data : result;
      }

      // Handle objects
      const sanitized = {};
      // Anything that is not a plain object (class instance, null prototype, Map)
      // is still rewritten to a plain object, as before
      let unchanged = Object.getPrototypeOf(data) === Object.prototype;
      for (const key in data) {
        // Skip React/DOM properties
        if (key.startsWith('_react') || key.startsWith('__react') ||
            key === 'window' || key === 'document' || key === 'toJSON' ||
            key === '_owner' || key === '_store' || key === '_self' ||
            key === '_source' || key === 'ref' || key === 'key' ||
            key === 'props' && data[key]?.$$typeof) {
          unchanged = false;
          continue;
        }

        try {
          if (data.hasOwnProperty(key)) {
            const value = data[key];
            const sanitizedValue = this.sanitizeData(value, seen);
            if (sanitizedValue !== null && sanitizedValue !== undefined) {
              sanitized[key] = sanitizedValue;
              if (sanitizedValue !== value) unchanged = false;
            } else {
              // Value dropped (null, undefined, DOM, circular)
              unchanged = false;
            }
          } else {
            // Inherited enumerable property, dropped as before
            unchanged = false;
          }
        } catch (e) {
          unchanged = false;
          console.warn(`SmartCache: Error sanitizing property '${key}':`, e.message);
        }
      }

      seen.delete(data);
      return unchanged ? data : sanitized;
    } catch (error) {
      seen.delete(data);
      console.error('SmartCache: Error during sanitization:', error);
      return null;
    }
  }

  /**
   * Deep clone data safely, avoiding circular references and DOM elements
   * @param {*} obj - Object to clone
   * @param {WeakSet} seen - Track visited objects
   * @returns {*} Cloned object
   */
  deepCloneSafe(obj, seen = new WeakSet()) {
    // Handle primitives
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj);
    if (obj instanceof RegExp) return new RegExp(obj);

    // Check for circular references
    if (seen.has(obj)) {
      return null; // Return null for circular references
    }
    
    // Skip DOM elements and React internals
    if (obj instanceof Element || obj instanceof Window || 
        obj._reactInternalFiber || obj._reactFiber || 
        obj.$$typeof || obj._owner || obj.nativeEvent ||
        obj.currentTarget || obj.target) {
      return null; // Don't clone DOM/React objects
    }
    
    seen.add(obj);
    
    try {
      // Handle arrays
      if (Array.isArray(obj)) {
        const result = obj.map(item => this.deepCloneSafe(item, seen));
        seen.delete(obj);
        return result;
      }
      
      // Handle objects
      const cloned = {};
      for (const key in obj) {
        // Skip React/DOM properties
        if (key.startsWith('_react') || key.startsWith('__react') ||
            key === 'window' || key === 'document' || key === 'toJSON') {
          continue;
        }
        
        try {
          if (obj.hasOwnProperty(key)) {
            cloned[key] = this.deepCloneSafe(obj[key], seen);
          }
        } catch (e) {
          // Skip properties that throw errors
          console.warn(`SmartCache: Skipping property '${key}' during clone:`, e.message);
        }
      }
      
      seen.delete(obj);
      return cloned;
    } catch (error) {
      seen.delete(obj);
      console.error('SmartCache: Error during deep clone:', error);
      return null;
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      cachedPresentations: this.data.size,
      dirtyPresentations: this.dirtyData.size,
      activeSubscribers: this.subscribers.size,
      fieldSubscribers: this.countFieldSubscribers(),
      pendingSyncs: this.syncTimers.size,
      memoryUsage: this.estimateMemoryUsage()
    };
  }

  /**
   * Debug: Get field subscription details
   */
  getFieldSubscriptionDetails() {
    const details = {};
    this.fieldSubscribers.forEach((fields, presentationId) => {
      details[presentationId] = {};
      fields.forEach((callbacks, fieldName) => {
        details[presentationId][fieldName] = callbacks.size;
      });
    });
    return details;
  }

  /**
   * Count total field subscribers
   */
  countFieldSubscribers() {
    let count = 0;
    this.fieldSubscribers.forEach(fields => {
      fields.forEach(callbacks => {
        count += callbacks.size;
      });
    });
    return count;
  }

  /**
   * Estimate memory usage
   */
  estimateMemoryUsage() {
    let bytes = 0;
    this.data.forEach(presentation => {
      bytes += JSON.stringify(presentation).length;
    });
    return `~${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }

  /**
   * STREAMING SESSION MANAGEMENT (Frontend-First Architecture)
   * These methods implement the streaming guard pattern to prevent
   * Firestore onSnapshot overwrites during active AI streaming sessions.
   */

  /**
   * Mark a presentation as having an active streaming session
   * @param {string} presentationId - Presentation ID
   * @param {string} streamId - Optional unique stream ID (generated if not provided)
   * @returns {string} The stream ID to use for cleanup
   */
  markStreamingActive(presentationId, streamId = null) {
    if (!this.streamingGuardEnabled) return null;

    // Generate unique stream ID if not provided
    const sid = streamId || `stream-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

    // Get or create presentation session tracker
    if (!this.activeStreamingSessions.has(presentationId)) {
      this.activeStreamingSessions.set(presentationId, { streams: new Map() });
    }

    const session = this.activeStreamingSessions.get(presentationId);
    session.streams.set(sid, {
      startTime: Date.now(),
      lastUpdate: Date.now()
    });

    // Track metrics
    this.streamingMetrics.sessionsStarted++;

    console.log(`[SmartCache] Streaming session started: ${presentationId} (streamId: ${sid}, total active: ${session.streams.size})`);

    return sid;
  }

  /**
   * Mark a streaming session as complete (allows Firestore sync)
   * @param {string} presentationId - Presentation ID
   * @param {string} streamId - The stream ID to mark as complete
   */
  markStreamingComplete(presentationId, streamId) {
    if (!this.streamingGuardEnabled) return;

    const session = this.activeStreamingSessions.get(presentationId);
    if (!session) {
      console.warn(`[SmartCache] No active session found for ${presentationId}`);
      return;
    }

    // Handle null streamId gracefully (legacy compatibility)
    if (!streamId) {
      console.warn(`[SmartCache] markStreamingComplete called without streamId for ${presentationId}, clearing all streams`);
      this.activeStreamingSessions.delete(presentationId);
      return;
    }

    const streamData = session.streams.get(streamId);
    if (streamData) {
      const duration = Date.now() - streamData.startTime;

      // Track metrics
      this.streamingMetrics.sessionsCompleted++;
      this.streamingMetrics.totalDuration += duration;

      session.streams.delete(streamId);

      console.log(`[SmartCache] Stream ${streamId} completed for ${presentationId} (duration: ${duration}ms, remaining: ${session.streams.size})`);

      // Only delete presentation entry when ALL streams are done
      if (session.streams.size === 0) {
        this.activeStreamingSessions.delete(presentationId);
        console.log(`[SmartCache] All streams complete for ${presentationId} - guard released`);
      }
    } else {
      console.warn(`[SmartCache] Stream ${streamId} not found in session for ${presentationId}`);
    }
  }

  /**
   * Check if a presentation has an active streaming session
   * @param {string} presentationId - Presentation ID
   * @returns {boolean} True if streaming is active (any stream exists)
   */
  isStreamingActive(presentationId) {
    if (!this.streamingGuardEnabled) return false;
    const session = this.activeStreamingSessions.get(presentationId);
    return session && session.streams && session.streams.size > 0;
  }

  /**
   * Update the last activity timestamp for a streaming session
   * @param {string} presentationId - Presentation ID
   * @param {string} streamId - Optional stream ID to update (updates all if not provided)
   */
  touchStreamingSession(presentationId, streamId = null) {
    if (!this.streamingGuardEnabled) return;

    const session = this.activeStreamingSessions.get(presentationId);
    if (session && session.streams) {
      if (streamId) {
        // Update specific stream
        const streamData = session.streams.get(streamId);
        if (streamData) {
          streamData.lastUpdate = Date.now();
        }
      } else {
        // Update all streams (legacy compatibility)
        session.streams.forEach((streamData) => {
          streamData.lastUpdate = Date.now();
        });
      }
    }
  }

  /**
   * Get debug information about active streaming sessions
   * @returns {Object} Debug info with session details
   */
  getStreamingDebugInfo() {
    const presentations = {};
    let totalStreams = 0;

    this.activeStreamingSessions.forEach((session, presentationId) => {
      const streams = {};
      session.streams.forEach((streamData, streamId) => {
        streams[streamId] = {
          duration: Date.now() - streamData.startTime,
          lastUpdate: Date.now() - streamData.lastUpdate,
          startTime: new Date(streamData.startTime).toISOString(),
          lastUpdateTime: new Date(streamData.lastUpdate).toISOString()
        };
        totalStreams++;
      });

      presentations[presentationId] = {
        streamCount: session.streams.size,
        streams
      };
    });

    return {
      enabled: this.streamingGuardEnabled,
      activePresentationCount: this.activeStreamingSessions.size,
      totalActiveStreams: totalStreams,
      presentations
    };
  }

  /**
   * Enable or disable the streaming guard (for rollback capability)
   * @param {boolean} enabled - Whether to enable the guard
   */
  setStreamingGuardEnabled(enabled) {
    this.streamingGuardEnabled = enabled;
    console.log(`[SmartCache] Streaming guard ${enabled ? 'enabled' : 'disabled'}`);

    if (!enabled) {
      // Clear all sessions when disabling
      this.activeStreamingSessions.clear();
    }
  }

}

// Export singleton instance
export const smartCache = new SmartCache();
export default SmartCache;
